import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { settingsService } from '../lib/services';

const CONSENT_KEY = 'cc_consent';

const defaultConfig = {
  cookieConsentEnabled: 'true',
  cookieConsentPosition: 'bottom',
  cookieConsentStyle: 'bar',
  cookieConsentTitle: 'We use cookies',
  cookieConsentMessage:
    'We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. By clicking "Accept All", you consent to our use of cookies.',
  cookieConsentAcceptLabel: 'Accept All',
  cookieConsentRejectLabel: 'Reject Non-Essential',
  cookieConsentManageLabel: 'Manage Preferences',
  cookieConsentColor: '#6366f1',
  cookieAnalyticsEnabled: 'true',
  cookieMarketingEnabled: 'false',
  cookieFunctionalEnabled: 'true',
};

const CookieConsentBanner = () => {
  const [config, setConfig] = useState(defaultConfig);
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, functional: true, analytics: true, marketing: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await settingsService?.getByCategory('gdpr');
        if (data?.length) {
          const map = {};
          data?.forEach(s => {
            const key = s?.settingKey?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase());
            map[key] = s?.settingValue;
          });
          setConfig(prev => ({ ...prev, ...map }));
        }
      } catch (e) {
        // use defaults
      } finally {
        setLoaded(true);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    if (config?.cookieConsentEnabled !== 'true') return;
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setVisible(true);
    }
  }, [loaded, config?.cookieConsentEnabled]);

  const saveConsent = (accepted) => {
    const consent = {
      necessary: true,
      functional: accepted ? config?.cookieFunctionalEnabled !== 'false' : false,
      analytics: accepted ? config?.cookieAnalyticsEnabled !== 'false' : false,
      marketing: accepted ? config?.cookieMarketingEnabled === 'true' : false,
      timestamp: new Date()?.toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
    setShowPreferences(false);
  };

  const saveCustomPrefs = () => {
    const consent = { ...prefs, necessary: true, timestamp: new Date()?.toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
    setShowPreferences(false);
  };

  if (!visible || config?.cookieConsentEnabled !== 'true') return null;

  const positionClasses = {
    bottom: 'bottom-0 left-0 right-0',
    top: 'top-0 left-0 right-0',
    'bottom-left': 'bottom-4 left-4 max-w-md',
    'bottom-right': 'bottom-4 right-4 max-w-md',
  };

  const isBar = config?.cookieConsentStyle === 'bar';
  const isModal = config?.cookieConsentStyle === 'modal';

  const posClass = positionClasses?.[config?.cookieConsentPosition] || positionClasses?.['bottom'];
  const accentColor = config?.cookieConsentColor || '#6366f1';

  return (
    <>
      {isModal && <div className="fixed inset-0 bg-black/50 z-[9998]" />}
      <div
        className={`fixed z-[9999] ${isModal ? 'inset-0 flex items-center justify-center p-4' : posClass}`}
        role="dialog"
        aria-label="Cookie consent"
      >
        <div
          className={`bg-card border border-border shadow-2xl ${
            isModal
              ? 'rounded-xl max-w-lg w-full'
              : isBar
              ? 'w-full' :'rounded-xl max-w-md w-full'
          } p-5 md:p-6`}
        >
          {showPreferences ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">Cookie Preferences</h3>
                <button onClick={() => setShowPreferences(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={18} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Choose which cookies you allow. Necessary cookies cannot be disabled.
              </p>
              <div className="space-y-3">
                {[
                  { key: 'necessary', label: 'Necessary', desc: 'Required for the site to function. Cannot be disabled.', locked: true },
                  { key: 'functional', label: 'Functional', desc: 'Enable enhanced functionality and personalisation.' },
                  { key: 'analytics', label: 'Analytics', desc: 'Help us understand how visitors interact with the site.' },
                  { key: 'marketing', label: 'Marketing', desc: 'Used to deliver relevant advertisements.' },
                ]?.map(item => (
                  <div key={item?.key} className="flex items-start justify-between gap-3 p-3 bg-muted/30 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item?.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item?.desc}</p>
                    </div>
                    <button
                      disabled={item?.locked}
                      onClick={() => !item?.locked && setPrefs(p => ({ ...p, [item?.key]: !p?.[item?.key] }))}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors mt-0.5 ${
                        item?.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      style={{ backgroundColor: prefs?.[item?.key] ? accentColor : '#6b7280' }}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                          prefs?.[item?.key] ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={saveCustomPrefs}
                  className="flex-1 py-2 px-4 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accentColor }}
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => saveConsent(false)}
                  className="flex-1 py-2 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors"
                >
                  Reject All
                </button>
              </div>
            </div>
          ) : (
            <div className={`${isBar ? 'flex flex-col md:flex-row md:items-center gap-4' : 'space-y-4'}`}>
              <div className={`flex items-start gap-3 ${isBar ? 'flex-1' : ''}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accentColor}20` }}>
                  <Icon name="Cookie" size={16} style={{ color: accentColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground mb-1">{config?.cookieConsentTitle}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{config?.cookieConsentMessage}</p>
                </div>
              </div>
              <div className={`flex flex-wrap gap-2 ${isBar ? 'flex-shrink-0' : ''}`}>
                <button
                  onClick={() => saveConsent(true)}
                  className="py-2 px-4 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap"
                  style={{ backgroundColor: accentColor }}
                >
                  {config?.cookieConsentAcceptLabel}
                </button>
                <button
                  onClick={() => saveConsent(false)}
                  className="py-2 px-4 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors whitespace-nowrap"
                >
                  {config?.cookieConsentRejectLabel}
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="py-2 px-4 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                >
                  {config?.cookieConsentManageLabel}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;
